import * as React from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useTranslations } from '@/hooks/use-translations'
import { useCreateUtilityTemplate, useUtilityTemplatesQuery } from '@/queries/meta'

const PARAM = /\{\{(\d+)\}\}/g

function paramCount(body: string): number {
  return new Set(Array.from(body.matchAll(PARAM), (m) => m[1])).size
}

/**
 * Messenger utility templates of the Page (`pages_utility_messaging`): the
 * only way to write to someone outside the 24-hour window about a service
 * matter. Meta reviews each one in seconds.
 */
export function UtilityTemplatesTab() {
  const { t } = useTranslations()
  const templates = useUtilityTemplatesQuery()
  const create = useCreateUtilityTemplate()
  const [name, setName] = React.useState('')
  const [body, setBody] = React.useState('')
  const [examples, setExamples] = React.useState<string[]>([])
  const [errors, setErrors] = React.useState<{ name?: string; body?: string }>({})
  const count = paramCount(body)

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const next = {
      name: /^[a-z0-9_]+$/.test(name) ? undefined : t('facebook_page.utility.name_invalid'),
      body: body.trim() && examples.slice(0, count).every((e) => e.trim()) && examples.length >= count
        ? undefined
        : t('facebook_page.utility.body_invalid'),
    }
    setErrors(next)
    if (next.name || next.body) return
    create.mutate(
      { name, language: 'es', body, examples: examples.slice(0, count) },
      {
        onSuccess: (created) => {
          toast.success(t('facebook_page.utility.created', { status: created.status }))
          setName('')
          setBody('')
          setExamples([])
        },
        onError: (err) => toast.error(`${t('facebook_page.utility.create_error')}: ${err.message}`),
      }
    )
  }

  return (
    <div className="space-y-8">
      <form onSubmit={submit} className="max-w-2xl space-y-4" noValidate>
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">{t('facebook_page.utility.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('facebook_page.utility.description')}</p>
        </div>

        <div className="space-y-1">
          <Label htmlFor="utility-name">{t('facebook_page.utility.name')}</Label>
          <Input
            id="utility-name"
            aria-label={t('facebook_page.utility.name')}
            value={name}
            placeholder={t('facebook_page.utility.name_placeholder')}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'utility-name-error' : undefined}
            onChange={(event) => setName(event.target.value.toLowerCase())}
          />
          <FieldError id="utility-name-error">{errors.name}</FieldError>
        </div>

        <div className="space-y-1">
          <Label htmlFor="utility-body">{t('facebook_page.utility.body')}</Label>
          <Textarea
            id="utility-body"
            aria-label={t('facebook_page.utility.body')}
            rows={3}
            value={body}
            placeholder={t('facebook_page.utility.body_placeholder')}
            aria-invalid={!!errors.body}
            aria-describedby={errors.body ? 'utility-body-error' : undefined}
            onChange={(event) => setBody(event.target.value)}
          />
          <p className="text-xs text-muted-foreground">{t('facebook_page.utility.body_help')}</p>
          <FieldError id="utility-body-error">{errors.body}</FieldError>
        </div>

        {Array.from({ length: count }, (_, index) => (
          <div key={index} className="space-y-1">
            <Label htmlFor={`utility-example-${index}`}>{t('facebook_page.utility.example', { n: index + 1 })}</Label>
            <Input
              id={`utility-example-${index}`}
              aria-label={t('facebook_page.utility.example', { n: index + 1 })}
              value={examples[index] ?? ''}
              onChange={(event) =>
                setExamples((current) => {
                  const next = [...current]
                  next[index] = event.target.value
                  return next
                })
              }
            />
          </div>
        ))}

        <Button type="submit" disabled={create.isPending}>{t('facebook_page.utility.create')}</Button>
      </form>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">{t('facebook_page.utility.list_title')}</h2>
        {templates.isLoading && <Skeleton className="h-24 w-full" />}
        {templates.error && (
          <p className="text-sm text-destructive">{t('facebook_page.load_error')}: {templates.error.message}</p>
        )}
        {templates.data?.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('facebook_page.utility.empty')}</p>
        )}
        <ul className="space-y-2">
          {templates.data?.map((template) => (
            <li key={template.id ?? template.name} className="space-y-1 rounded-lg border p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold">{template.name}</span>
                <span className="text-xs text-muted-foreground">{template.language}</span>
                <Badge variant={template.status === 'APPROVED' ? 'default' : 'outline'}>{template.status}</Badge>
              </div>
              <p className="text-sm">{template.body}</p>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}
